import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { IconLoading } from '@/icons';
import { Flex } from '@/ui/Flex/Flex';

type Props = {
  readonly children?: React.ReactNode;
  readonly size?: 'small' | 'medium' | 'large' | 'minimum';
  readonly href?: string;
  readonly type?: 'button' | 'submit';
  readonly loading?: boolean;
  readonly icon?: React.ReactNode;

  readonly onClick?: React.MouseEventHandler<
    HTMLButtonElement | HTMLAnchorElement
  >;
  readonly mode?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'danger'
    | 'link'
    | 'transparent'
    | 'ghost';
  readonly className?: string;
  readonly testId?: string;
  readonly disabled?: boolean;
};

const StyledWrapper = styled.button<Pick<Props, 'disabled' | 'size' | 'mode'>>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.1s ease;
  > span {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  ${({ mode, theme }) => {
    switch (mode) {
      case 'secondary':
        return css`
          color: ${theme.color('#1B1A1F')};
          background-color: ${theme.color('#F6F6F6')};

          &:hover {
            background-color: ${theme.color('#F9F7F7')};
          }
        `;

      case 'danger':
        return css`
          color: ${theme.color('#FFFFFF')};
          background-color: ${theme.color('#DD6C6C')};
          font-weight: 600;

          &:hover {
            background-color: ${theme.color('#CD5555')};
          }
        `;

      case 'outline':
        return css`
          color: ${theme.color('#1B1A1F')};
          background-color: ${theme.color('#FFFFFF')};
          font-weight: 600;
          outline: solid 1px ${theme.color('#1B1A1F')};
          border-radius: 40px;

          &:hover {
            background-color: ${theme.color('#F2F0F0')};
          }
        `;

      case 'ghost':
        return css`
          background-color: ${theme.color('#FFFFFF')};
          border: solid 1px rgba(55, 55, 55, 0.2);

          &:hover {
            background-color: ${theme.color('#F2F0F0')};
          }
        `;

      case 'link':
        return css`
          color: ${theme.color('#1B1A1F')};
          background-color: transparent;
          text-decoration: underline;
          font-weight: 600;

          &:hover {
            background-color: transparent;
            opacity: 0.8;
          }
        `;

      case 'transparent':
        return css`
          background-color: transparent;

          &:hover {
            opacity: 0.8;
          }
        `;

      case 'primary':
        return css`
          color: ${theme.color('#FFFFFF')};
          background-color: ${theme.color('#239AE7')};

          &:hover {
            background-color: ${theme.color('#158AD6')};
          }
        `;
      default:
        return css``;
    }
  }}

  ${({ size }) => {
    switch (size) {
      case 'large':
        return css`
          min-height: 40px;
          padding: 4px 14px;
          font-size: 16px;
        `;

      case 'medium':
        return css`
          min-height: 32px;
          padding: 4px 14px;
          font-size: 14px;
        `;

      case 'small':
        return css`
          min-height: 26px;
          padding: 4px 14px;
          font-size: 12px;
        `;
      case 'minimum':
        return css`
          width: fit-content;
        `;
      default:
        return css``;
    }
  }}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}
`;

const StyledLoadingWrapper = styled(Flex)<Pick<Props, 'size' | 'mode'>>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  ${({ mode, theme }) => {
    switch (mode) {
      case 'secondary':
        return css`
          stroke: ${theme.color('#676570')};
        `;

      case 'danger':
        return css`
          stroke: ${theme.color('#FFFFFF')};
        `;

      case 'outline':
        return css`
          stroke: ${theme.color('#1B1A1F')};
        `;

      case 'link':
        return css`
          stroke: ${theme.color('#1B1A1F')};
        `;

      case 'transparent':
        return css`
          stroke: ${theme.color('#1B1A1F')};
        `;

      case 'primary':
        return css`
          stroke: ${theme.color('#FFFFFF')};
        `;

      default:
        return css``;
    }
  }}

  ${({ size }) => {
    switch (size) {
      case 'large':
        return css`
          svg {
            width: 26px;
            height: 26px;
          }
        `;

      case 'medium':
        return css`
          svg {
            width: 20px;
            height: 20px;
          }
        `;

      case 'small':
        return css`
          svg {
            width: 16px;
            height: 16px;
          }
        `;

      default:
        return css``;
    }
  }}
`;

export const Button = ({
  href,
  mode = 'primary',
  size = 'medium',
  type = 'button',
  onClick,
  children,
  loading = false,
  className,
  testId = 'test-button',
  disabled,
  icon,
}: Props) => (
  <StyledWrapper
    as={typeof href === 'string' ? NavLink : undefined}
    className={className}
    data-testid={testId}
    disabled={disabled}
    mode={mode}
    onClick={onClick}
    size={size}
    to={href!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
    type={type}
  >
    {loading ? (
      <StyledLoadingWrapper
        align="center"
        justify="center"
        mode={mode}
        size={size}
      >
        <IconLoading />
      </StyledLoadingWrapper>
    ) : null}

    <span style={loading ? { visibility: 'hidden' } : undefined}>
      {icon || null}
      {children}
    </span>
  </StyledWrapper>
);
